import { SupportedLanguage } from './SupportedLanguage';

const allSupportedLanguages = Object.values(SupportedLanguage);

export function parseSupportedLanguage(
  value: string | undefined,
): SupportedLanguage | undefined {
  if (!value) {
    return undefined;
  }

  return (
    matchExactLanguageCode(value) ??
    matchLegacyLanguageCode(value) ??
    matchNorwegianVariant(value) ??
    matchNormalizedLanguageTag(value) ??
    matchPrimaryLanguageSubtag(value)
  );
}

/**
 * Exact match against a known enum value (case-sensitive).
 *
 * @example
 * matchExactLanguageCode('en-US'); // SupportedLanguage.English
 * matchExactLanguageCode('zh-Hant-TW'); // SupportedLanguage.ChineseTraditionalTaiwan
 * matchExactLanguageCode('en-us'); // undefined (casing differs)
 * matchExactLanguageCode('xx'); // undefined (not a known code)
 */
function matchExactLanguageCode(value: string): SupportedLanguage | undefined {
  return allSupportedLanguages.includes(value as SupportedLanguage)
    ? (value as SupportedLanguage)
    : undefined;
}

/**
 * Match legacy language codes to their corresponding SupportedLanguage.
 * For example, the legacy code 'ua' maps to SupportedLanguage.Ukrainian.
 *
 * @param value The language code to match against legacy mappings.
 * @returns The corresponding SupportedLanguage if a legacy mapping exists, otherwise undefined.
 */
function matchLegacyLanguageCode(value: string): SupportedLanguage | undefined {
  const transformMap: Record<string, SupportedLanguage> = {
    ua: SupportedLanguage.Ukrainian,
  };

  return transformMap[value.toLowerCase()];
}

/**
 * Norwegian Bokmal (nb) and Nynorsk (nn) both map to Norwegian.
 *
 * @example
 * matchNorwegianVariant('nb'); // SupportedLanguage.Norwegian
 * matchNorwegianVariant('nn-NO'); // SupportedLanguage.Norwegian
 * matchNorwegianVariant('NB'); // SupportedLanguage.Norwegian (case-insensitive)
 * matchNorwegianVariant('sv'); // undefined (not a Norwegian variant)
 */
function matchNorwegianVariant(value: string): SupportedLanguage | undefined {
  const lowerCaseValue = value.toLowerCase();
  const primarySubtag = normalizeSeparatorsToDash(lowerCaseValue).split('-')[0];

  return primarySubtag === 'nb' || primarySubtag === 'nn'
    ? SupportedLanguage.Norwegian
    : undefined;
}

/**
 * Match the full tag ignoring casing, underscore/dash separators, and the
 * Chinese script subtag (e.g. `zh-cn` -> `zh-Hans-CN`, `zh_tw` -> `zh-Hant-TW`).
 *
 * @example
 * matchNormalizedLanguageTag('EN-US'); // SupportedLanguage.English (casing ignored)
 * matchNormalizedLanguageTag('pt_pt'); // SupportedLanguage.Portuguese (underscores normalized)
 * matchNormalizedLanguageTag('zh-cn'); // SupportedLanguage.ChineseSimplifiedChina (script subtag stripped)
 * matchNormalizedLanguageTag('zh-tw'); // SupportedLanguage.ChineseTraditionalTaiwan (script subtag stripped)
 * matchNormalizedLanguageTag('pt'); // undefined (short code, handled by matchPrimaryLanguageSubtag)
 */
function matchNormalizedLanguageTag(
  value: string,
): SupportedLanguage | undefined {
  const lowerCaseValue = value.toLowerCase();
  const dashSeparatedValue = normalizeSeparatorsToDash(lowerCaseValue);

  return allSupportedLanguages.find((supportedLanguage) => {
    const lowerCaseLanguage = supportedLanguage.toLowerCase();

    return (
      lowerCaseValue === lowerCaseLanguage ||
      dashSeparatedValue === lowerCaseLanguage ||
      lowerCaseLanguage.replace('-hant', '') === lowerCaseValue ||
      lowerCaseLanguage.replace('-hans', '') === lowerCaseValue
    );
  });
}

/**
 * Fall back to a prefix / primary-subtag match, so short codes (`pt`, `en`) or
 * differing full locales (`sv-SE`, `zh-CN`) still resolve to a language.
 *
 * Because it only compares the primary subtag (the part before the first dash),
 * it cannot distinguish regional variants: it returns the FIRST entry in
 * `SupportedLanguage` declaration order that shares that primary subtag. Any
 * region/script the app does not model separately therefore collapses onto that
 * first entry (see the Chinese examples, where the script can even be wrong).
 *
 * @example
 * // Basic matches:
 * matchPrimaryLanguageSubtag('pt'); // SupportedLanguage.Portuguese (prefix of 'pt-PT')
 * matchPrimaryLanguageSubtag('en'); // SupportedLanguage.English (prefix of 'en-US')
 * matchPrimaryLanguageSubtag('sv-SE'); // SupportedLanguage.Swedish (shared primary subtag 'sv')
 * matchPrimaryLanguageSubtag('fr_CA'); // SupportedLanguage.French (shared primary subtag 'fr')
 * matchPrimaryLanguageSubtag('xx'); // undefined (no matching language)
 *
 * @example
 * // Edge cases - regional variants are NOT distinguished, all collapse to the
 * // single supported entry that shares the primary subtag:
 * matchPrimaryLanguageSubtag('pt-BR'); // SupportedLanguage.Portuguese (Brazil -> pt-PT, only Portuguese exists)
 * matchPrimaryLanguageSubtag('en-GB'); // SupportedLanguage.English (British -> en-US, only English exists)
 * matchPrimaryLanguageSubtag('es-MX'); // SupportedLanguage.Spanish (Mexican -> es-ES, only Spanish exists)
 * matchPrimaryLanguageSubtag('fr-CA'); // SupportedLanguage.French (Canadian -> fr-FR, only French exists)
 *
 * @example
 * // Chinese - three variants exist (zh-Hans-CN, zh-Hant-HK, zh-Hant-TW), but
 * // any zh-* not already resolved by matchNormalizedLanguageTag falls to the
 * // FIRST declared Chinese entry, regardless of the actual region or script:
 * matchPrimaryLanguageSubtag('zh'); // SupportedLanguage.ChineseSimplifiedChina (first 'zh' entry)
 * matchPrimaryLanguageSubtag('zh-MO'); // SupportedLanguage.ChineseSimplifiedChina (Macau is Traditional, but still maps to Simplified)
 * matchPrimaryLanguageSubtag('zh-Hant-MO'); // SupportedLanguage.ChineseSimplifiedChina (script is ignored here)
 */
function matchPrimaryLanguageSubtag(
  value: string,
): SupportedLanguage | undefined {
  const lowerCaseValue = value.toLowerCase();
  const primarySubtagOfValue =
    normalizeSeparatorsToDash(lowerCaseValue).split('-')[0];

  return allSupportedLanguages.find((supportedLanguage) => {
    const lowerCaseLanguage = supportedLanguage.toLowerCase();

    return (
      lowerCaseLanguage.startsWith(lowerCaseValue) ||
      primarySubtagOfValue === lowerCaseLanguage.split('-')[0]
    );
  });
}

/**
 * Replace underscore separators with dashes so tags use a consistent format,
 * matching the dash-separated locale codes in `SupportedLanguage`.
 *
 * @example
 * normalizeSeparatorsToDash('pt_pt'); // 'pt-pt'
 * normalizeSeparatorsToDash('zh_hans_cn'); // 'zh-hans-cn'
 * normalizeSeparatorsToDash('en-us'); // 'en-us' (unchanged, no underscores)
 */
function normalizeSeparatorsToDash(value: string): string {
  return value.replace(/_/g, '-');
}
